/**
 * The sections of the mobile Brief feed, in the order they are interleaved.
 *
 * ONE definition, imported by both the feed builder (`App.js`'s `mobileBadgeItems`
 * memo) and the picker (`MobileFeedSettings.jsx`). The repo's recurring bug is two
 * hand-written copies of one rule silently disagreeing — here that would show up as
 * a toggle in the sheet that turns nothing off, or a group with no toggle at all, so
 * the builder derives its groups from this list rather than restating it.
 *
 * `key` names the GROUP, not the item `type`: buy and sell signals are two groups
 * whose items carry the types `signal` / `sellSignal` (those drive the card's icon
 * and colour in MobileBadgeTape's TYPE_CONFIG and are deliberately separate).
 */
export const MOBILE_FEED_SECTIONS = [
  { key: 'summary', label: 'Daily summary', description: "Your list's focus and the macro pulse" },
  { key: 'sentiment', label: 'Fear & Greed', description: 'Overall sentiment score and its indicators' },
  { key: 'breadth', label: 'Market breadth', description: 'How much of the market is advancing' },
  { key: 'cycle', label: 'Market cycle', description: 'Current phase and any transition' },
  { key: 'sector', label: 'Sectors', description: 'Leading industries, leadership and rotation' },
  { key: 'risk', label: 'Risks & weakness', description: 'Lagging industries and key observations' },
  { key: 'macro', label: 'Macro scenarios', description: 'Probability-weighted scenarios and global watch' },
  { key: 'market', label: 'Market analysis', description: 'AI summary, key events and top stories' },
  { key: 'bond', label: 'Bonds & yields', description: 'Yield curve slope and the desk take' },
  { key: 'buckets', label: 'Asset classes', description: 'Where money rushed in and out over the last 5 sessions' },
  { key: 'news', label: 'Market news', description: 'General market headlines' },
  { key: 'event', label: 'Economic events', description: 'Releases and events in the next 7 days' },
  { key: 'stockNews', label: 'Stocks in the news', description: 'Headlines matched to stocks you follow' },
  { key: 'movers', label: 'Your movers', description: "Today's biggest gainers and losers in your list" },
  { key: 'listMovers', label: 'Smart lists on the move', description: 'Today\'s leading and lagging smart lists' },
  { key: 'callHighlight', label: 'Earnings call takeaways', description: 'One-line read on calls your stocks just held' },
  { key: 'guidanceSeason', label: 'Guidance this season', description: 'How many companies raised, kept or lowered their outlook' },
  { key: 'portfolioEarnings', label: 'Your upcoming earnings', description: 'Stocks you follow reporting within 7 days' },
  { key: 'buySignal', label: 'Buy signals', description: 'Strong technical buy setups in your list' },
  { key: 'sellSignal', label: 'Sell signals', description: 'Strong technical sell setups in your list' },
];

/**
 * Enabled unless explicitly switched off.
 *
 * Reading absence as ON (rather than defaulting a stored map to the full set once) is
 * what lets a NEW section appear for a user whose saved map predates it. The inverse —
 * treating a missing key as off — would silently hide every future section behind a
 * setting the user never saw.
 */
export const isFeedSectionEnabled = (sections, key) => sections?.[key] !== false;

export const countEnabledSections = (sections) =>
  MOBILE_FEED_SECTIONS.filter((section) => isFeedSectionEnabled(sections, section.key)).length;
